import { ApiError } from "@chai/api-client";
import type { SignupRequest } from "@chai/shared";
import { Link, createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { z } from "zod";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApi } from "../../api";
import { useAuth } from "../../auth";
import { useDocumentTitle } from "@/lib/document-title";

export const Route = createFileRoute("/_auth/signup")({
  component: SignUpPage,
  validateSearch: z.object({ token: z.string().optional() }),
});

function SignUpPage() {
  useDocumentTitle(["Create account"]);
  const api = useApi();
  const auth = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_auth/signup" });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inviteToken = search.token;
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [orgName, setOrgName] = useState("");
  const [token, setToken] = useState(inviteToken ?? "");
  // MUST sit with the other hooks, ABOVE the early returns below. It used to
  // live after them, so the first render (authConfig still null) ran seven
  // hooks and the next ran eight — React error #310, "Rendered more hooks
  // than during the previous render", which crashed /signup to a bare
  // "Something went wrong" on every cold load in production. The initial
  // value cannot read authConfig for the same reason, so it keys off the
  // invite token alone and the create/join default is applied below.
  const [choice, setChoice] = useState<"create" | "join" | null>(null);

  if (!auth.authConfig) {
    return (
      <div className="flex justify-center pt-12">
        <Spinner />
      </div>
    );
  }

  if (!auth.authConfig.allowSignup) {
    // Two different audiences, two different truths. On a demo hostname the
    // visitor is a prospect who already HAS credentials and simply landed on
    // the wrong page — telling them to "contact your administrator" is both
    // wrong and a dead end.
    const onDemo = auth.authConfig.isDemoHost;
    return (
      <Card className="mt-10">
        <CardHeader>
          <CardTitle>
            {onDemo ? "This is a demo — no account needed" : "Signup is disabled"}
          </CardTitle>
          <CardDescription>
            {onDemo
              ? "Accounts aren't created here. Sign in with the email and password you were given for this demo — they were sent with your invitation."
              : "Contact your administrator for an invitation."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/signin" className={buttonVariants({ variant: "brand" })}>
            {onDemo ? "Go to sign in" : "Back to sign in"}
          </Link>
        </CardContent>
      </Card>
    );
  }

  const canCreate = auth.authConfig.allowOrgCreation && !inviteToken;
  const canJoin = !!inviteToken || auth.authConfig.requireInvite;
  // Resolved, not stored: `choice` starts null (see the hook above) and only
  // becomes state once the visitor actually picks. Until then it derives from
  // the config, which is what the old post-guard useState was really doing.
  const effectiveChoice: "create" | "join" =
    choice ?? (inviteToken ? "join" : canCreate ? "create" : "join");

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      const payload: SignupRequest = {
        email,
        password,
        name,
        org_choice: effectiveChoice,
        org_name: effectiveChoice === "create" ? orgName : undefined,
        invite_token: effectiveChoice === "join" ? token : undefined,
      };
      await api.signup(payload);
      await api.login(payload.email, payload.password).catch(() => undefined);
      await auth.refresh();
      void navigate({ to: "/workspaces" });
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError((err as Error).message);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 pt-10">
      <header>
        <p className="meta-strip mb-2">Get started</p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          Create your account
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {auth.authConfig.requireInvite
            ? "An invite token is required to join an existing org."
            : "Spin up an org or join one with an invite."}
        </p>
      </header>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} noValidate>
            <FieldGroup>
              {canCreate && canJoin && !inviteToken && (
                <Tabs value={effectiveChoice} onValueChange={(v) => setChoice(v as "create" | "join")}>
                  <TabsList className="w-full">
                    <TabsTrigger value="create" className="flex-1">
                      Create an org
                    </TabsTrigger>
                    <TabsTrigger value="join" className="flex-1">
                      Join with an invite
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              )}

              <Field>
                <FieldLabel htmlFor="signup-name">Your name</FieldLabel>
                <Input
                  id="signup-name"
                  autoComplete="name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="signup-email">Email</FieldLabel>
                <Input
                  id="signup-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="signup-password">Password</FieldLabel>
                <Input
                  id="signup-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <FieldDescription>At least 8 characters.</FieldDescription>
              </Field>

              {effectiveChoice === "create" ? (
                <Field>
                  <FieldLabel htmlFor="signup-org">Org name</FieldLabel>
                  <Input
                    id="signup-org"
                    required
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                  />
                </Field>
              ) : (
                <Field>
                  <FieldLabel htmlFor="signup-token">Invite token</FieldLabel>
                  <Input
                    id="signup-token"
                    required
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="Paste the invite token"
                  />
                </Field>
              )}

              {error && (
                <Alert variant="destructive">
                  <AlertTitle>Sign-up failed</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" disabled={pending} className="w-full">
                {pending && <Spinner data-icon="inline-start" />}
                Create account
              </Button>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>

      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{" "}
        <Link to="/signin" className="font-medium text-primary underline-offset-4 hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  );
}
